import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined"
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import ListItemButton from "@mui/material/ListItemButton"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import {
  format,
  formatDistanceToNow,
  isThisYear,
  isToday,
  isYesterday,
} from "date-fns"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link as RouterLink, useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { FOOTER_HEIGHT } from "../../../common/components/Footer"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import InboxChannelToggle from "./InboxChannelToggle"
import InboxContactChip from "./InboxContactChip"
import InboxReplyComposer from "./InboxReplyComposer"
import InboxTemplatesDrawer from "./InboxTemplatesDrawer"
import ProfileColumn from "./ProfileColumn"
import SmsInboxPanel from "./SmsInboxPanel"
import VoicemailInboxPanel from "./VoicemailInboxPanel"

function firstApiErrorMessage(data, fallback) {
  if (!data || typeof data !== "object") {
    return hideVendorName(fallback)
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return hideVendorName(data.detail)
  }
  for (const key of ["content", "to", "non_field_errors", "detail"]) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) {
      return hideVendorName(value)
    }
    if (Array.isArray(value) && value.length) {
      return hideVendorName(value.map(String).join(" "))
    }
  }
  return hideVendorName(fallback)
}

function hideVendorName(text) {
  return String(text || "")
    .replace(/\bZoho Mail\b/gi, "company email")
    .replace(/\bZoho\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^[:\-–—.\s]+/, "")
    .trim()
}

function decodeHtmlEntities(value) {
  if (!value) return ""
  const textarea = document.createElement("textarea")
  textarea.innerHTML = String(value)
  return textarea.value
}

function cleanSnippet(value) {
  return decodeHtmlEntities(String(value || "").replace(/<[^>]+>/g, " "))
    .replace(/\s+/g, " ")
    .trim()
}

function parseFrom(fromAddress) {
  const raw = decodeHtmlEntities(fromAddress || "").trim()
  const angled = raw.match(/^(?:"?([^"<]*)"?\s*)?<([^>]+)>$/)
  if (angled) {
    const email = (angled[2] || "").trim()
    const name = (angled[1] || "").trim()
    return { name: name || email.split("@")[0] || email, email }
  }
  const embedded = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)
  if (embedded) {
    const email = embedded[0]
    const name = raw.replace(email, "").replace(/[<>]/g, "").trim()
    return { name: name || email.split("@")[0] || email, email }
  }
  return { name: raw || "Unknown sender", email: raw }
}

function threadCounterparty(row) {
  if (row?.candidate_email) {
    return {
      name: row.candidate_name || parseFrom(row.candidate_email).name,
      email: String(row.candidate_email),
    }
  }
  if (row?.contact_name) {
    const parsed = parseFrom(row?.from_address)
    const outbound = parseFrom(row?.to_address)
    const email = row?.is_inbound
      ? parsed.email
      : outbound.email?.includes("@")
      ? outbound.email
      : parsed.email
    return { name: row.contact_name, email }
  }
  const inbound = parseFrom(row?.from_address)
  const outbound = parseFrom(row?.to_address)
  if (row?.is_inbound && inbound.email?.includes("@")) {
    return inbound
  }
  if (outbound.email?.includes("@")) {
    return outbound
  }
  return inbound
}

function initials(name, email) {
  const source = (name || email || "?").trim()
  const parts = source.split(/[\s@._-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

function formatListTime(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  if (isToday(date)) return format(date, "h:mm a")
  if (isYesterday(date)) return "Yesterday"
  if (isThisYear(date)) return format(date, "MMM d")
  return format(date, "MMM d, yyyy")
}

function formatMessageTime(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return format(date, "MMM d, yyyy · h:mm a")
}

function formatSyncLabel(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return `Updated ${formatDistanceToNow(date, { addSuffix: true })}`
}

const emailBodySx = {
  fontSize: 14,
  lineHeight: 1.65,
  color: "text.primary",
  wordBreak: "break-word",
  "& html, & body": { height: "auto", minHeight: "unset", overflow: "visible" },
  "& img": { maxWidth: "100%", height: "auto", borderRadius: 1 },
  "& a": { color: "primary.main" },
  "& p": { my: 1 },
  "& p:first-of-type": { mt: 0 },
  "& p:last-of-type": { mb: 0 },
  "& table": { maxWidth: "100%" },
  "& td, & th": { wordBreak: "break-word" },
  "& blockquote": {
    m: 0,
    pl: 1.5,
    borderLeft: "3px solid",
    borderColor: "colors.gray200",
    color: "text.secondary",
  },
}

export default function InboxTab() {
  const profile = useSelector(selectProfile)
  const axios = useAuthorizedAxiosClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const [mailbox, setMailbox] = useState(null)
  const [threads, setThreads] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [thread, setThread] = useState(null)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isReplying, setIsReplying] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [templatesRevision, setTemplatesRevision] = useState(0)
  const threadScrollRef = useRef(null)

  const concierge = isConcierge(profile)
  const channelParam = searchParams.get("channel")
  const channel =
    channelParam === "sms"
      ? "sms"
      : channelParam === "voicemail"
      ? "voicemail"
      : "email"
  const threadFromUrl = searchParams.get("thread")
  const showList = !isMobile || !selectedId
  const showReadingPane = !isMobile || Boolean(selectedId)

  function handleChannelChange(next) {
    const nextParams = new URLSearchParams(searchParams)
    if (next === "sms") {
      nextParams.set("channel", "sms")
    } else if (next === "voicemail") {
      nextParams.set("channel", "voicemail")
    } else {
      nextParams.delete("channel")
    }
    if (next !== channel) {
      nextParams.delete("thread")
      nextParams.delete("voicemail")
    }
    setSearchParams(nextParams)
  }

  const loadThreads = useCallback(
    (options = {}) => {
      const silent = Boolean(options.silent)
      if (!silent) setIsLoading(true)
      return axios
        .get("/crm/api/zoho-mail/inbox/threads/")
        .then(({ data }) => {
          setMailbox(data.mailbox)
          setThreads(data.results || [])
          setError("")
        })
        .catch((err) => {
          setMailbox(null)
          setThreads([])
          setError(
            firstApiErrorMessage(
              err?.response?.data,
              "Could not load inbox for this parent account."
            )
          )
        })
        .finally(() => {
          if (!silent) setIsLoading(false)
        })
    },
    [axios]
  )

  useEffect(() => {
    if (!concierge || channel === "sms" || channel === "voicemail")
      return undefined
    loadThreads()
    const intervalId = window.setInterval(() => {
      loadThreads({ silent: true })
    }, 60000)
    return () => window.clearInterval(intervalId)
  }, [channel, concierge, loadThreads, profile?.active_account])

  useEffect(() => {
    if (threadFromUrl) {
      setSelectedId(threadFromUrl)
    }
  }, [threadFromUrl])

  useEffect(() => {
    if (channel === "sms" || channel === "voicemail" || !selectedId) {
      setThread(null)
      return undefined
    }
    const threadId = selectedId
    function loadEmailThread({ silent = false } = {}) {
      axios
        .get(`/crm/api/zoho-mail/inbox/threads/${threadId}/`)
        .then(({ data }) => {
          setThread(data)
          if (data.unread_count) {
            axios
              .post(`/crm/api/zoho-mail/inbox/threads/${threadId}/read/`)
              .then(({ data: updated }) => {
                setThread(updated)
                setThreads((current) =>
                  current.map((row) =>
                    row.thread_id === threadId
                      ? { ...row, unread_count: 0, is_read: true }
                      : row
                  )
                )
              })
              .catch(() => {})
          }
        })
        .catch((err) => {
          if (silent) return
          setError(
            firstApiErrorMessage(err?.response?.data, "Could not open thread.")
          )
        })
    }
    loadEmailThread()
    const intervalId = window.setInterval(() => {
      loadEmailThread({ silent: true })
    }, 30000)
    return () => window.clearInterval(intervalId)
  }, [axios, channel, selectedId])

  function handleSync() {
    setIsSyncing(true)
    axios
      .post("/crm/api/zoho-mail/inbox/sync/")
      .then(({ data }) => {
        setMailbox(data.mailbox)
        setThreads(data.results || [])
        setError("")
      })
      .catch((err) => {
        setError(
          firstApiErrorMessage(err?.response?.data, "Could not sync inbox.")
        )
      })
      .finally(() => setIsSyncing(false))
  }

  function handleReply(html, templateId) {
    if (!selectedId || !html) return Promise.resolve(false)
    setIsReplying(true)
    return axios
      .post(`/crm/api/zoho-mail/inbox/threads/${selectedId}/reply/`, {
        content: html,
        template_id: templateId || null,
      })
      .then(({ data }) => {
        setThread(data)
        setThreads((current) => {
          const others = current.filter((row) => row.thread_id !== selectedId)
          return [data, ...others]
        })
        return true
      })
      .catch((err) => {
        setError(
          firstApiErrorMessage(err?.response?.data, "Could not send reply.")
        )
        return false
      })
      .finally(() => setIsReplying(false))
  }

  useEffect(() => {
    const pane = threadScrollRef.current
    if (!pane || !thread?.messages?.length) return
    pane.scrollTop = pane.scrollHeight
  }, [thread?.thread_id, thread?.messages?.length])

  if (!concierge) {
    return null
  }

  if (channel === "sms") {
    return (
      <SmsInboxPanel channel={channel} onChannelChange={handleChannelChange} />
    )
  }

  if (channel === "voicemail") {
    return (
      <VoicemailInboxPanel
        channel={channel}
        onChannelChange={handleChannelChange}
      />
    )
  }

  return (
    <ProfileColumn
      sx={{
        gap: 1.25,
        width: "100%",
        minHeight: 0,
        pt: 0.5,
        paddingBlockStart: 1,
        height: {
          xs: "calc(100dvh - 148px)",
          md: `calc(100dvh - ${FOOTER_HEIGHT} - 200px)`,
        },
        maxHeight: {
          xs: "calc(100dvh - 148px)",
          md: `calc(100dvh - ${FOOTER_HEIGHT} - 200px)`,
        },
        overflow: "hidden",
      }}
    >
      <Row
        sx={{
          gap: 2,
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Column sx={{ gap: 0.25, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Messages
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "colors.gray600",
              display: { xs: "none", sm: "block" },
            }}
          >
            Read and reply to email for this parent account. Switch to SMS or
            Voicemail for texts and messages on your calling number.
          </Typography>
        </Column>
        <Row sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {mailbox?.email && (
            <Chip
              size="small"
              label={mailbox.email}
              sx={{
                bgcolor: "colors.gray50",
                border: "1px solid",
                borderColor: "colors.gray200",
                color: "colors.gray700",
                fontWeight: 500,
              }}
            />
          )}
          {mailbox?.last_sync_at && (
            <Typography variant="caption" sx={{ color: "colors.gray500" }}>
              {formatSyncLabel(mailbox.last_sync_at)}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => setTemplatesOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Email templates
          </Button>
          <LoadingButton
            variant="outlined"
            size="small"
            startIcon={<RefreshOutlinedIcon />}
            loading={isSyncing}
            onClick={handleSync}
            disabled={!mailbox?.connected}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Sync
          </LoadingButton>
        </Row>
      </Row>
      <InboxChannelToggle value={channel} onChange={handleChannelChange} />

      {error && (
        <Alert severity="warning">
          {error}{" "}
          <Link component={RouterLink} to="/settings?tab=email">
            Open Email settings
          </Link>
        </Alert>
      )}
      {mailbox && !mailbox.connected && (
        <Alert severity="info">
          Connect this parent mailbox in Email settings before new mail can
          sync.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          border: "1px solid",
          borderColor: "colors.gray200",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
          flex: "1 1 0%",
          minHeight: 0,
          height: "100%",
        }}
      >
        {showList && (
          <Column
            sx={{
              width: { xs: "100%", md: 380 },
              flexShrink: 0,
              minHeight: 0,
              height: { md: "100%" },
              borderRight: { md: "1px solid" },
              borderColor: { md: "colors.gray200" },
              bgcolor: "colors.gray50",
              overflow: "auto",
            }}
          >
            <Row
              sx={{
                px: 2.5,
                py: 1.75,
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "colors.gray200",
                bgcolor: "background.paper",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.primary"
              >
                Conversations
              </Typography>
              <Typography variant="caption" sx={{ color: "colors.gray500" }}>
                {threads.length}
              </Typography>
            </Row>
            {isLoading ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  gap: 1.5,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ color: "colors.gray500" }}>
                  Loading inbox…
                </Typography>
              </Column>
            ) : threads.length === 0 ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  py: 8,
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "colors.brand50",
                    color: "primary.main",
                  }}
                >
                  <InboxOutlinedIcon />
                </Box>
                <Typography fontWeight={600} color="text.primary">
                  No messages yet
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "colors.gray600" }}
                >
                  Inbound mail for this parent mailbox will appear here after it
                  is connected.
                </Typography>
              </Column>
            ) : (
              threads.map((row) => (
                <ThreadRow
                  key={row.thread_id}
                  row={row}
                  selected={selectedId === row.thread_id}
                  onSelect={() => setSelectedId(row.thread_id)}
                />
              ))
            )}
          </Column>
        )}

        {showReadingPane && (
          <Column
            sx={{
              flex: "1 1 0%",
              minWidth: 0,
              minHeight: 0,
              height: { md: "100%" },
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            {!thread ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "colors.gray100",
                    color: "colors.gray400",
                  }}
                >
                  <InboxOutlinedIcon fontSize="large" />
                </Box>
                <Typography fontWeight={600} color="text.primary">
                  Select a conversation
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "colors.gray600", maxWidth: 360 }}
                >
                  Choose a thread on the left to read inbound mail and reply
                  without leaving CRM.
                </Typography>
              </Column>
            ) : (
              <Column
                sx={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <Column
                  sx={{
                    flexShrink: 0,
                    px: { xs: 2, md: 3 },
                    py: 1.25,
                    gap: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "colors.gray200",
                  }}
                >
                  <Row sx={{ alignItems: "flex-start", gap: 1 }}>
                    {isMobile && (
                      <IconButton
                        size="small"
                        onClick={() => setSelectedId(null)}
                        sx={{ mt: 0.25 }}
                        aria-label="Back to conversations"
                      >
                        <ArrowBackOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Column sx={{ gap: 0.75, minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="text.primary"
                        sx={{ lineHeight: 1.3 }}
                      >
                        {decodeHtmlEntities(thread.subject) || "(no subject)"}
                      </Typography>
                      <Row
                        sx={{
                          gap: 1,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <InboxContactChip
                          contact={thread}
                          fallbackLabel={threadCounterparty(thread).name}
                          unmatchedLabel="Unmatched — not linked to a candidate"
                        />
                        {threadCounterparty(thread).email?.includes("@") ? (
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ color: "colors.gray600", fontWeight: 600 }}
                          >
                            {threadCounterparty(thread).email}
                          </Typography>
                        ) : null}
                        {thread.message_count > 1 && (
                          <Typography
                            variant="caption"
                            sx={{ color: "colors.gray500" }}
                          >
                            {thread.message_count} messages
                          </Typography>
                        )}
                      </Row>
                    </Column>
                  </Row>
                </Column>

                <Column
                  ref={threadScrollRef}
                  sx={{
                    flex: "1 1 0%",
                    minHeight: 0,
                    overflowX: "hidden",
                    overflowY: "auto",
                    px: { xs: 2, md: 2.5 },
                    py: 1.5,
                    gap: 1.5,
                    bgcolor: "colors.dimBackground",
                  }}
                >
                  {(thread.messages || []).map((message) => (
                    <MessageCard key={message.id} message={message} />
                  ))}
                </Column>

                <Column
                  sx={{
                    flexShrink: 0,
                    zIndex: 2,
                    gap: 1,
                    px: { xs: 2, md: 2.5 },
                    py: 1.25,
                    borderTop: "1px solid",
                    borderColor: "colors.gray200",
                    bgcolor: "background.paper",
                  }}
                >
                  <InboxReplyComposer
                    key={`${thread.thread_id}-${templatesRevision}`}
                    thread={thread}
                    mailboxConnected={Boolean(mailbox?.connected)}
                    isReplying={isReplying}
                    templatesRevision={templatesRevision}
                    onManageTemplates={() => setTemplatesOpen(true)}
                    onSend={handleReply}
                  />
                </Column>
              </Column>
            )}
          </Column>
        )}
      </Paper>
      <InboxTemplatesDrawer
        open={templatesOpen}
        onClose={() => {
          setTemplatesOpen(false)
          setTemplatesRevision((current) => current + 1)
        }}
      />
    </ProfileColumn>
  )
}

function ThreadRow({ row, selected, onSelect }) {
  const unread = row.unread_count > 0
  const counterparty = threadCounterparty(row)
  const snippet = cleanSnippet(row.snippet)
  const showEmail =
    Boolean(counterparty.email?.includes("@")) &&
    String(counterparty.email).toLowerCase() !==
      String(counterparty.name || "").toLowerCase()

  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{
        alignItems: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.75,
        borderBottom: "1px solid",
        borderColor: "colors.gray200",
        bgcolor: selected ? "colors.brand50" : "background.paper",
        borderLeft: "3px solid",
        borderLeftColor: selected
          ? "primary.main"
          : unread
          ? "primary.light"
          : "transparent",
        "&.Mui-selected": {
          bgcolor: "colors.brand50",
        },
        "&.Mui-selected:hover": {
          bgcolor: "colors.brand100",
        },
        "&:hover": {
          bgcolor: selected ? "colors.brand100" : "colors.gray50",
        },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          mt: 0.25,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: unread ? "primary.main" : "colors.gray200",
          color: unread ? "common.white" : "colors.gray700",
        }}
      >
        {initials(counterparty.name, counterparty.email)}
      </Avatar>
      <Column sx={{ minWidth: 0, flex: 1, gap: 0.35 }}>
        <Row sx={{ alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: showEmail ? "0 1 auto" : 1,
              maxWidth: showEmail ? "46%" : "100%",
              minWidth: 0,
              fontWeight: unread ? 700 : 600,
              color: "text.primary",
            }}
          >
            {counterparty.name}
          </Typography>
          {showEmail ? (
            <Typography
              variant="caption"
              noWrap
              title={counterparty.email}
              sx={{
                flex: 1,
                minWidth: 0,
                color: unread ? "primary.main" : "colors.gray600",
                fontWeight: unread ? 600 : 500,
              }}
            >
              {counterparty.email}
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            sx={{
              flexShrink: 0,
              color: unread ? "primary.main" : "colors.gray500",
              fontWeight: unread ? 700 : 500,
            }}
          >
            {formatListTime(row.latest_at)}
          </Typography>
        </Row>
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: unread ? 600 : 500,
            color: "text.primary",
          }}
        >
          {decodeHtmlEntities(row.subject) || "(no subject)"}
        </Typography>
        {snippet ? (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: "colors.gray500", display: "block" }}
          >
            {snippet}
          </Typography>
        ) : null}
      </Column>
      {unread ? (
        <Box
          sx={{
            width: 8,
            height: 8,
            mt: 1,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: "primary.main",
          }}
        />
      ) : null}
    </ListItemButton>
  )
}

function MessageCard({ message }) {
  const from = parseFrom(message.from_address)
  const inbound = Boolean(message.is_inbound)
  const html = message.body_html || cleanSnippet(message.snippet)

  return (
    <Paper
      elevation={0}
      sx={{
        alignSelf: inbound ? "flex-start" : "flex-end",
        width: "100%",
        maxWidth: { xs: "100%", md: "92%" },
        border: "1px solid",
        borderColor: inbound ? "colors.gray200" : "colors.brand200",
        bgcolor: inbound ? "background.paper" : "colors.brand50",
        borderRadius: 2,
        p: { xs: 1.5, md: 1.75 },
      }}
    >
      <Row sx={{ alignItems: "center", gap: 1.25, mb: 1 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: inbound ? "colors.gray200" : "primary.main",
            color: inbound ? "colors.gray700" : "common.white",
          }}
        >
          {initials(from.name, from.email)}
        </Avatar>
        <Column sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            noWrap
          >
            {from.name}
          </Typography>
          <Typography variant="caption" sx={{ color: "colors.gray500" }} noWrap>
            {from.email}
            {message.received_at
              ? ` · ${formatMessageTime(message.received_at)}`
              : ""}
          </Typography>
        </Column>
        <Chip
          size="small"
          label={inbound ? "Inbound" : "Sent"}
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: inbound ? "colors.gray100" : "colors.brand100",
            color: inbound ? "colors.gray700" : "primary.dark",
          }}
        />
      </Row>
      <Divider
        sx={{
          mb: 1.25,
          borderColor: inbound ? "colors.gray200" : "colors.brand200",
        }}
      />
      <Box sx={emailBodySx} dangerouslySetInnerHTML={{ __html: html }} />
    </Paper>
  )
}
